
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FilmInventory } from "@/types/inventory";
import { UseFormReturn } from "react-hook-form";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { z } from "zod";
import DOMPurify from "dompurify";

// Define the schema for the transfer form to match TransferFilmDialog
const transferFormSchema = z.object({
  quantity: z.number().min(1, { message: "Must transfer at least 1 roll" }),
  notes: z.string().optional(),
});

// Define the type from the schema
type TransferFormValues = z.infer<typeof transferFormSchema>;

interface TransferFilmFormProps {
  film: FilmInventory;
  form: UseFormReturn<TransferFormValues>;
  onSubmit: (values: TransferFormValues) => Promise<void>;
}

export function TransferFilmForm({ film, form, onSubmit }: TransferFilmFormProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [charCount, setCharCount] = useState(form.getValues("notes")?.length || 0);
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [notesError, setNotesError] = useState<string | null>(null);
  const maxChars = 100;

  const validateQuantity = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    
    if (isNaN(numValue)) {
      setQuantityError("Quantity must be a number");
      return false;
    }
    
    if (numValue < 1) {
      setQuantityError("Must transfer at least 1 roll");
      return false;
    }
    
    if (film && numValue > film.quantity) {
      setQuantityError(`Cannot exceed available quantity (${film.quantity})`);
      return false;
    }
    
    setQuantityError(null);
    return true;
  };
  
  const validateNotes = (value: string) => {
    if (!value) {
      setNotesError(null);
      return true;
    }
    
    if (value.length > maxChars) {
      setNotesError(`Notes cannot exceed ${maxChars} characters`);
      return false;
    }
    
    // Check for potential XSS or script content
    const sanitized = DOMPurify.sanitize(value);
    if (sanitized !== value) {
      setNotesError("Notes contain potentially unsafe content");
      return false;
    }
    
    // Check for specific patterns that might indicate script injection
    const maliciousPatterns = [
      /<script/i,
      /javascript:/i,
      /onclick/i,
      /onerror/i,
      /eval\(/i
    ];

    for (const pattern of maliciousPatterns) {
      if (pattern.test(value)) {
        setNotesError("Notes contain potentially unsafe content");
        return false;
      }
    }
    
    setNotesError(null);
    return true;
  };
  
  // Initialize validation on mount
  useEffect(() => {
    const initialQuantity = form.getValues("quantity");
    const initialNotes = form.getValues("notes");
    
    if (initialQuantity) {
      validateQuantity(initialQuantity);
    }
    
    if (initialNotes) {
      setCharCount(initialNotes.length);
      validateNotes(initialNotes);
    }
  }, []);

  const handleFormSubmit = async (values: TransferFormValues) => {
    const isQuantityValid = validateQuantity(values.quantity);
    const isNotesValid = validateNotes(values.notes || "");
    
    if (isQuantityValid && isNotesValid) {
      await onSubmit(values);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <div>
          <h3 className="font-medium mb-1">{t('transferring')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {film.brand} {film.film_name} ({film.film_type}, ISO {film.iso})
          </p>
        </div>

        <FormField
          control={form.control}
          name="quantity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('quantity_expose')}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={film.quantity}
                  className={`${isMobile ? "text-base" : ""} ${quantityError ? "border-destructive" : ""}`}
                  {...field}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    validateQuantity(value);
                    field.onChange(value);
                  }}
                  onBlur={() => validateQuantity(field.value)}
                  value={field.value}
                />
              </FormControl>
              {quantityError && (
                <p className="text-xs font-medium text-destructive">{quantityError}</p>
              )}
              <FormDescription className="text-xs text-muted-foreground">
                Available: {film.quantity} {film.quantity === 1 ? 'roll' : 'rolls'}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('notes_optional')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add details about what was shot"
                  className={`resize-none ${isMobile ? "text-base" : ""} ${notesError ? "border-destructive" : ""}`}
                  {...field}
                  maxLength={maxChars}
                  value={field.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    setCharCount(value.length);
                    validateNotes(value);
                  }}
                  onBlur={() => validateNotes(field.value || "")}
                />
              </FormControl>
              <div className={`text-xs ${charCount > maxChars ? "text-destructive" : "text-muted-foreground"} text-right mt-1`}>
                {charCount}/{maxChars}
              </div>
              {notesError && (
                <p className="text-xs font-medium text-destructive">{notesError}</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-4">
          <Button 
            type="submit"
            disabled={!!quantityError || !!notesError}
          >
            {t('mark_exposed')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
